import {
  ABNORMAL_COLOR_ORDER,
  ABNORMAL_COLORS,
  type AbnormalColor,
  DEFAULT_ABNORMAL,
} from '@quickreport/domain';
import clsx from 'clsx';
import { Check } from 'lucide-react';

/**
 * How a value outside its normal range looks on a printed report: bold or not, and a colour.
 * Used in Report Settings (for all reports) and for a single result or report. It holds no state:
 * the page decides what to do with the choice.
 */
export interface AbnormalLook {
  bold: boolean;
  color: AbnormalColor;
}

export const SAME_LOOK_AS_NORMAL = (l: AbnormalLook) => !l.bold && l.color === 'black';

/** Words for a look: "bold red", "blue", "plain black". */
export function describeLook(l: AbnormalLook): string {
  const colour = ABNORMAL_COLORS[l.color].label.toLowerCase();
  if (l.bold) return `bold ${colour}`;
  return l.color === 'black' ? 'plain black' : colour;
}

/** A little piece of a report: a normal result above an out-of-range one, in the chosen look. */
export function AbnormalPreview({ look }: { look: AbnormalLook }) {
  const style = {
    color: ABNORMAL_COLORS[look.color].hex,
    fontWeight: look.bold ? 700 : 400,
  } as const;
  return (
    <div
      className="overflow-hidden rounded-lg border border-line bg-white text-[13px] text-black"
      aria-label="How it will look"
    >
      <div className="flex items-baseline gap-3 border-b border-line/70 px-3 py-1.5">
        <span className="flex-1">Haemoglobin</span>
        <span className="w-12 text-center">13.6</span>
        <span className="w-14 text-slate">g/dL</span>
      </div>
      <div className="flex items-baseline gap-3 px-3 py-1.5">
        <span className="flex-1">Platelet Count</span>
        <span className="w-12 text-center" style={style}>
          113
        </span>
        <span className="w-14 text-slate">
          <span style={style}>L</span>
        </span>
      </div>
    </div>
  );
}

/** The two choices, with a picture of the result: a tick for bold, and four colours. */
export function AbnormalPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: AbnormalLook;
  onChange: (next: AbnormalLook) => void;
  disabled?: boolean;
}) {
  const plain = SAME_LOOK_AS_NORMAL(value);
  return (
    <div className={clsx('grid gap-3', disabled && 'pointer-events-none opacity-50')}>
      <label className="flex min-h-10 cursor-pointer items-center gap-3 rounded-lg border border-line bg-paper px-3">
        <input
          type="checkbox"
          className="h-4 w-4 accent-brand"
          checked={value.bold}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, bold: e.target.checked })}
        />
        <span>
          <span className="block font-medium">Bold</span>
          <span className="block text-[12px] text-slate">Print the value in heavier letters</span>
        </span>
      </label>

      <fieldset className="grid gap-1.5" disabled={disabled}>
        <legend className="mb-1 text-[12px] font-medium text-slate">Colour</legend>
        <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label="Colour">
          {ABNORMAL_COLOR_ORDER.map((c) => {
            const on = value.color === c;
            return (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => onChange({ ...value, color: c })}
                className={clsx(
                  'grid min-h-[4.25rem] place-items-center gap-1 rounded-xl border px-1 py-2 text-[12px] font-medium transition-colors',
                  on
                    ? 'border-brand bg-brand-soft text-navy'
                    : 'border-line bg-paper hover:bg-canvas',
                )}
              >
                <span
                  className="grid h-7 w-7 place-items-center rounded-full ring-1 ring-black/10"
                  style={{ background: ABNORMAL_COLORS[c].hex }}
                >
                  {on ? <Check size={15} className="text-white" strokeWidth={3} /> : null}
                </span>
                {ABNORMAL_COLORS[c].label}
              </button>
            );
          })}
        </div>
      </fieldset>

      <AbnormalPreview look={value} />
      {plain ? (
        <p className="rounded-md bg-amber-50 px-3 py-2 text-[12px] text-warn">
          With plain black letters, out-of-range values look the same as normal ones. Only the H or
          L next to them shows the difference.
        </p>
      ) : (
        <p className="text-[12px] text-slate">
          Normal values always print in plain black. Only values outside the normal range use this.
        </p>
      )}
    </div>
  );
}

export const DEFAULT_LOOK: AbnormalLook = { ...DEFAULT_ABNORMAL };
